import {createBrowserRouter, redirect } from "react-router-dom";

import { authProvider } from "./auth";
import { PATHS } from './paths'
import { genericLoader } from "./utils";

// TODO: move things here once i have refactored them
import { LoginPage, loginLoader, loginAction } from "./pages/Login";
import { LogoutPage } from "./pages/Logout";
import { Info } from "./pages/Info";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Terms } from "./pages/Terms";
import { Support } from "./pages/Support";
import { GetTheApp } from "./pages/GetTheApp";
import { Funnel } from "./pages/Funnel";
import { ErrorPage } from "./pages/ErrorPage";

// Shared
import { Classes } from "./pages/shared/Classes";
import { Leadership } from "./pages/shared/Leadership";
import { Partnerships } from "./pages/admin/Partnerships";
import { CourseDeliveryAudit } from "./pages/admin/CourseDeliveryAudit";
import { Profile } from "./pages/shared/Profile";
import { Gallery } from "./pages/shared/Gallery";
import { TagImage } from "./pages/shared/Gallery/TagImage";
import { Class } from "./pages/shared/Class";
import { People } from "./pages/shared/People";
import { Birthdays } from "./pages/shared/Birthday";
import { TimeTable } from "./pages/admin/TimeTable";
import { PostCompose } from "./pages/admin/Posts/PostCompose";
import { PostReviewView } from "./pages/admin/Posts/PostReview";
import { PostReviewsView } from "./pages/admin/Posts/PostReviews";
import { PostScheduledView } from "./pages/admin/Posts/PostScheduled";
import { PostSentView } from "./pages/admin/Posts/PostSent";
import { PostsScheduledView } from "./pages/admin/Posts/PostsScheduled";
import { PostEngagementView } from "./pages/admin/Posts/PostEngagement";
import { AdminSettings } from "./pages/admin/Settings";
import { RolesView } from "./pages/admin/Roles";
import { StreamsView } from "./pages/admin/Streams";
import { BlockAttendance } from "./pages/admin/BlockAttendance";
import { AttendanceCompare } from "./pages/admin/AttendanceCompare";
import { ClassRecord, PersonRecords, AllRecords } from "./pages/shared/Records"

// Admin only
import { SignUpPage, signupLoader, signupAction } from "./components/Signup";
import { ResetPassword } from "./pages/ResetPassword";
import { ForgotPassword } from "./pages/ForgotPassword";
import { DeleteAccount } from "./pages/DeleteAccount";
import { Organisations } from "./components/Protected/Organisations";
import { Overview } from "./components/Protected/Overview";
import { PersonCreator } from "./components/Protected/PersonCreator";
import { AdminTools } from "./components/Protected/AdminTools";
import { Dashboard } from "./components/Protected/Dashboard";
import { Attendance } from "./components/Protected/AttendanceDashboard";
import { Onboarding } from "./components/Protected/Onboarding";
import { Tags } from "./components/Protected/Tags";
import { Layout } from "./components/Layout";

const generic = (path: string, component: any) => ({
  path,
  loader: genericLoader,
  Component: component,
})

const redirector = ({from, to}: { from: string, to: string }) => ({
  path: from,
  Component: null,
  async loader() {
    return redirect(to);
  },
})

// /android and /ios are speakable aliases for /app that hint the intended platform via ?p=.
// The guide itself still trusts the User-Agent over ?p= — someone on an iPhone who scans a
// printed /android poster must land on iOS instructions, not Android ones.
const platformRedirector = ({ from, platform }: { from: string, platform: "android" | "ios" }) => ({
  path: from,
  Component: null,
  async loader({ params }: { params: { code?: string } }) {
    const codePart = params.code ? `/${params.code}` : "";
    return redirect(`${PATHS.GET_THE_APP}${codePart}?p=${platform}`);
  },
})

const router = createBrowserRouter([
  {
    id: "root",
    path: PATHS.ROOT,
    errorElement: <ErrorPage />,
    Component: Layout,
    children: [
      // Public routes
      { index: true, action: loginAction, loader: loginLoader, Component: LoginPage },
      { path: PATHS.LOGIN, action: loginAction, loader: loginLoader, Component: LoginPage },
      { path: PATHS.SIGNUP, action: signupAction, loader: signupLoader, Component: SignUpPage },
      { path: PATHS.FORGOT_PASSWORD, Component: ForgotPassword },
      { path: PATHS.RESET_PASSWORD, Component: ResetPassword },
      { path: PATHS.DELETE_ACCOUNT, Component: DeleteAccount },
      { path: PATHS.INFO, Component: Info },
      { path: PATHS.PRIVACY_POLICY, Component: PrivacyPolicy },
      { path: PATHS.TERMS, Component: Terms },
      { path: PATHS.SUPPORT, Component: Support },
      { path: PATHS.GET_THE_APP, Component: GetTheApp },
      { path: PATHS.GET_THE_APP_CODE, Component: GetTheApp },
      platformRedirector({ from: PATHS.ANDROID, platform: "android" }),
      platformRedirector({ from: PATHS.ANDROID_CODE, platform: "android" }),
      platformRedirector({ from: PATHS.IOS, platform: "ios" }),
      platformRedirector({ from: PATHS.IOS_CODE, platform: "ios" }),
      { path: PATHS.FUNNEL, Component: Funnel },

      // Protected routes
        // All users
        generic(PATHS.ONBOARDING, Onboarding),
        generic(PATHS.ORGANISATIONS, Organisations),
        generic(PATHS.CLASSES, Classes),
        generic(PATHS.CLASS, Class),
        generic(PATHS.PEOPLE, People),
        generic(PATHS.BIRTHDAYS, Birthdays),
        generic(PATHS.ALL_RECORDS, AllRecords),
        generic(PATHS.CLASS_RECORDS, ClassRecord),
        generic(PATHS.PERSON_RECORDS, PersonRecords),
        generic(PATHS.POSTS_CREATE, PostCompose),
        generic(PATHS.POST_REVIEW, PostReviewView),
        generic(PATHS.POST_REVIEWS, PostReviewsView),
        // Content-reviewer only — gated within the page component (useContentReviewerGate)
        generic(PATHS.POST_SCHEDULED, PostScheduledView),
        generic(PATHS.POST_SENT, PostSentView),
        generic(PATHS.POSTS_SCHEDULED, PostsScheduledView),
        // Leadership or content reviewer — gated within the page component
        generic(PATHS.POSTS_ENGAGEMENT, PostEngagementView),
        generic(PATHS.ADMIN_SETTINGS, AdminSettings),
        // Gated inside the component on `canAdministerRoles` — the server's own
        // `mayGrant` answer, so the page and the mutations cannot drift apart.
        generic(PATHS.ADMIN_ROLES, RolesView),
        // Gated inside the component on reaching any stream — the same construction as
        // the gallery page: the list and the gate are one answer.
        generic(PATHS.ADMIN_STREAMS, StreamsView),
        generic(PATHS.PERSON, Profile),
        generic(PATHS.GALLERY, Gallery),
        generic(PATHS.TAG_IMAGE, TagImage),
        // Admin routes
        generic(PATHS.PEOPLE_CREATE, PersonCreator),
        generic(PATHS.ADMIN_TOOLS, AdminTools),
        generic(PATHS.DASHBOARD, Dashboard),
        generic(PATHS.ATTENDANCE, Attendance),
        // Block attendance (admin only — gated within the page component)
        generic(PATHS.BLOCK_ATTENDANCE, BlockAttendance),
        generic(PATHS.ATTENDANCE_COMPARE, AttendanceCompare),
        generic(PATHS.TAGS, Tags),
        generic(PATHS.CLASS_CREATE, TimeTable),

        // Supporter routes
        generic(PATHS.OVERVIEW, Overview),

        // Leadership routes
        generic(PATHS.LEADERSHIP, Leadership),

        // Partnerships (admin only — gated within the page component)
        generic(PATHS.PARTNERSHIPS, Partnerships),

        // Course delivery audit (admin only — gated within the page component)
        generic(PATHS.COURSE_DELIVERY_AUDIT, CourseDeliveryAudit),

      // Legacy versions, can remove in next week or to : from September 2025 feel free to remove these
      redirector({ from: PATHS.COURSES, to: PATHS.CLASSES }),
      redirector({ from: PATHS.COURSE, to: PATHS.CLASSES }),
    ],
  },
  {
    path: PATHS.LOGOUT,
    Component: LogoutPage,
    async loader() {
      await authProvider.signout();
      return null
    },
  },
]);


export { router };
